import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import AdminToolbar from '@/src/components/layout/AdminToolbar'
import { Label } from '@/src/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/src/components/ui/select'
import { Spinner } from '@/src/components/ui/spinner'
import { me } from '@/src/api/user'
import { userCanManageOrganizationSettings } from '@/src/lib/permissionHelpers'
import { OperationTypeUpsertForm } from '@/src/components/manage/forms/OperationTypeUpsertForm'
import { languages } from '@/i18next.config'

const LOCALES = [...new Set(languages.map((l) => l.split('-')[0]))]

export const Route = createFileRoute('/_manage/manage/organization/operation-types_/new')({
  component: OrgOperationTypeNewPage,
})

function OrgOperationTypeNewPage() {
  const navigate = useNavigate()
  const [locale, setLocale] = useState<string>(LOCALES[0] || 'en')

  const { data: user, isLoading } = useQuery({
    queryKey: ['user', 'me'],
    queryFn: () => me(),
  })

  const permissions: string[] = (user?.role?.permissions as string[]) ?? []
  const isAdmin = permissions.includes('*')
  const canManageOrg = userCanManageOrganizationSettings(permissions, isAdmin)
  const orgId = user?.organization?.id

  useEffect(() => {
    if (!isLoading && (!canManageOrg || !orgId)) {
      navigate({ to: '/not-permitted' })
    }
  }, [isLoading, canManageOrg, orgId, navigate])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Spinner />
      </div>
    )
  }

  if (!canManageOrg || !orgId) return null

  const handleSuccess = () => {
    navigate({ to: '/manage/organization/operation-types' })
  }

  const handleCancel = () => {
    navigate({ to: '/manage/organization/operation-types' })
  }

  return (
    <div className="flex flex-col h-full">
      <AdminToolbar>
        <AdminToolbar.Left>
          <AdminToolbar.Title>New Operation Type</AdminToolbar.Title>
        </AdminToolbar.Left>
        <AdminToolbar.Right>
          <div className="flex items-center gap-2 h-9">
            <Label className="text-sm leading-none whitespace-nowrap pb-0">Language</Label>
            <Select value={locale} onValueChange={(value) => setLocale(value || LOCALES[0] || 'en')}>
              <SelectTrigger className="w-32 h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LOCALES.map((entry) => (
                  <SelectItem key={entry} value={entry}>
                    {entry.toUpperCase()}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </AdminToolbar.Right>
      </AdminToolbar>

        <div className="flex-1 min-h-0">
            <div className="h-full overflow-auto">
          <OperationTypeUpsertForm
            organizationId={orgId}
            onCancel={handleCancel}
            onSuccess={handleSuccess}
            locale={locale}
            onLocaleChange={setLocale}
          />
        </div>
      </div>
    </div>
  )
}
